import { Route } from "../routes";

export enum Requires {
  Company = "RequiresCompany",
  CompletedOnboarding = "RequiresCompletedOnboarding",
  PermanentUser = "PermanentUser",
  Puzzle = "Puzzle",
  CompanyAdmin = "CompanyAdmin",
  Whitelist = "Whitelist",
  Notifications = "Notifications",
  IntegrationsPageVisibility = "RequiresIntegrationsPageVisibility",
  UsersPageVisibility = "RequiresUsersPageVisibility",
  NonClientViewer = "NonClientViewer", // initially for Decimal clients
  // might consider making this more generic like "Editor"
  // will consider with RED-1154
  FirmOwner = "FirmOwner",
  NonFirmOwner = "NonFirmOwner",
}

const minimumDashboardPermissions = [
  Requires.Company,
  Requires.PermanentUser,
  Requires.CompletedOnboarding,
  Requires.Whitelist,
];

const minimumDashboardPermissionsWithNonClientViewer = [
  Requires.Company,
  Requires.PermanentUser,
  Requires.CompletedOnboarding,
  Requires.Whitelist,
  Requires.NonClientViewer,
];

const minimumDashboardPermissionsWithAdmin = [
  ...minimumDashboardPermissions,
  Requires.CompanyAdmin,
];

export const RequiredPermissions: Record<Route, Requires[]> = {
  [Route.integrations]: [
    Requires.Company,
    Requires.CompletedOnboarding,
    Requires.PermanentUser,
    Requires.IntegrationsPageVisibility,
  ],
  [Route.connectPlaid]: [Requires.Company],
  [Route.connectBrex]: [Requires.Company],
  [Route.connectFinicity]: [Requires.Company],
  [Route.connectQuickbooks]: [Requires.Company],
  [Route.connectStripe]: [Requires.Company],
  [Route.connectGusto]: [Requires.Company],
  [Route.connectRippling]: [Requires.Company],
  [Route.linkRippling]: [Requires.Company],
  [Route.connectionError]: [Requires.Company],
  [Route.aiLabs]: minimumDashboardPermissions,
  [Route.lottie]: [],
  [Route.sync]: [],
  [Route.token]: [],
  [Route.inbox]: minimumDashboardPermissions,
  [Route.login]: [],
  [Route.logout]: [],
  [Route.me]: [],
  [Route.error]: [],
  [Route.landing]: [],
  [Route.noAccess]: minimumDashboardPermissions,
  [Route.createCompany]: [Requires.PermanentUser],
  [Route.companySettings]: minimumDashboardPermissionsWithAdmin,
  [Route.automationSettings]: minimumDashboardPermissionsWithAdmin,
  [Route.notificationSettings]: minimumDashboardPermissions,
  [Route.classesSettings]: minimumDashboardPermissionsWithAdmin,
  [Route.historicalBooks]: minimumDashboardPermissionsWithAdmin,
  [Route.historicalBooksError]: minimumDashboardPermissionsWithAdmin,
  [Route.dataSettings]: minimumDashboardPermissionsWithAdmin,
  [Route.otherSettings]: minimumDashboardPermissionsWithAdmin,
  [Route.intro]: [Requires.PermanentUser],
  [Route.accountDetails]: minimumDashboardPermissions,
  [Route.checklist]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.reports]: minimumDashboardPermissions,
  [Route.report]: minimumDashboardPermissions,
  [Route.printableReport]: minimumDashboardPermissions,
  [Route.transactions]: minimumDashboardPermissions,
  [Route.transactionsImport]: minimumDashboardPermissions,
  [Route.transactionsDetail]: minimumDashboardPermissions,
  [Route.creditCardCrossSell]: minimumDashboardPermissions,
  [Route.rules]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.rulesSettings]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.payroll]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.firmBookkeepers]: [...minimumDashboardPermissions, Requires.FirmOwner],
  [Route.firmGroups]: [...minimumDashboardPermissions, Requires.FirmOwner],
  [Route.usersSettings]: [
    Requires.CompanyAdmin,
    Requires.UsersPageVisibility,
    ...minimumDashboardPermissions,
  ],
  [Route.revenue]: minimumDashboardPermissions,
  [Route.accrualRevenue]: minimumDashboardPermissions,
  [Route.mrrRevenue]: minimumDashboardPermissions,
  [Route.mrrRevenueByCustomer]: minimumDashboardPermissions,
  [Route.expenses]: [...minimumDashboardPermissions, Requires.Puzzle],
  [Route.people]: minimumDashboardPermissions,
  [Route.ltse]: [],
  [Route.authorizePartner]: [],
  [Route.partnerOnboarding]: [],
  [Route.angelListConnect]: [],
  [Route.introConnect]: [],
  [Route.angellistCategorize]: [],
  [Route.genericConnect]: [],
  [Route.acmeConnect]: [],
  [Route.connectPartnerDataSource]: [],
  [Route.genericCategorize]: [],
  [Route.emailVerification]: [],
  [Route.emailNotifications]: [],
  [Route.emailVerificationSuccess]: [],
  [Route.notFound]: [],
  [Route.redirect]: [],
  [Route.home]: minimumDashboardPermissions,
  [Route.companyPrivateRoute]: minimumDashboardPermissions,
  [Route.accounting]: minimumDashboardPermissions,
  [Route.chartOfAccounts]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.chartOfAccountsSettings]: minimumDashboardPermissions,
  [Route.generalLedger]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.spending]: minimumDashboardPermissions,
  [Route.vendorList]: minimumDashboardPermissions,
  [Route.vendors]: minimumDashboardPermissions,
  [Route.vendors1099]: minimumDashboardPermissions,
  [Route.ledgerReconciliations]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.ledgerReconciliationsByAccount]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.newLedgerReconciliation]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.ledgerReconciliationViewer]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.manualJournals]: minimumDashboardPermissionsWithAdmin,
  [Route.newManualJournal]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.groupedMJE]: minimumDashboardPermissionsWithAdmin,
  [Route.newGroupedMJE]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.editGroupedMJE]: minimumDashboardPermissionsWithAdmin,
  [Route.lockedPeriod]: minimumDashboardPermissions,
  [Route.lockedPeriodSettings]: minimumDashboardPermissions,
  [Route.connectSuccess]: minimumDashboardPermissions,
  [Route.auditLog]: minimumDashboardPermissions,
  [Route.billing]: [...minimumDashboardPermissionsWithAdmin, Requires.NonFirmOwner],
  [Route.bills]: minimumDashboardPermissions,
  [Route.newBill]: minimumDashboardPermissions,
  [Route.angellistManage]: [],
  [Route.fixedAssets]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.fixedAssetsTransactions]: minimumDashboardPermissionsWithNonClientViewer,
  [Route.prepaidExpenses]: minimumDashboardPermissions,
  [Route.prepaidExpensesTransactions]: minimumDashboardPermissions,
  [Route.newPayment]: minimumDashboardPermissions,
  [Route.newInvoicePayment]: minimumDashboardPermissions,
  [Route.newBillPayment]: minimumDashboardPermissions,
  [Route.invoices]: minimumDashboardPermissions,
  [Route.newInvoice]: minimumDashboardPermissions,
  [Route.askAccountant]: minimumDashboardPermissions,
  [Route.apAging]: minimumDashboardPermissions,
  [Route.stripe]: minimumDashboardPermissions,
  [Route.stripeDetail]: minimumDashboardPermissions,
  [Route.stripeReconciliations]: minimumDashboardPermissions,
  [Route.stripeFullDetails]: minimumDashboardPermissions,
  [Route.icons]: [Requires.Puzzle],
  [Route.arAging]: minimumDashboardPermissions,
  [Route.settings]: minimumDashboardPermissions,
  [Route.accountingConfigurations]: minimumDashboardPermissions,
  [Route.policySettings]: minimumDashboardPermissions,
  [Route.newPolicy]: minimumDashboardPermissions,
  [Route.revenueRecognition]: minimumDashboardPermissions,
  [Route.customers]: minimumDashboardPermissions,
  [Route.products]: minimumDashboardPermissions,
  [Route.transactionsOnboarding]: minimumDashboardPermissions,
  [Route.transactionsShortcut]: minimumDashboardPermissions,
};
